<template>
  <section class="form-section grid-block">
    <header> Расчётные счета
      <VLink
        material="add"
        :disabled="createBankAccountDisabledReason !== null"
        :title="createBankAccountDisabledReason"
        @click="createBankAccount"
      >
        Добавить расчётный счёт
      </VLink>
    </header>

    <div class="content">
      <VDataGridV2
        :data="companiesWithAccounts"
        :context-menu="BankAccountsService.contextMenu"
        group-children-property="bankAccounts"
        layout="fill"
        @row:dblclick="row => BankAccountsService.showBankAccountCard(row, localPartner)"
        @contextMenuItem:done="onContextMenuItemDone"
      >
        <VDataGridGroupV2>
          <template #default="{ parent }">
            <div>{{ parent.name }}</div>
          </template>
        </VDataGridGroupV2>

        <VDataGridColumnV2
          id="account"
          label="Расчётный счёт"
        >
          <template #default="{ row }">
            <template v-if="row.account">
              <div :title="row.account">
                {{ $core.utils.regional.ru.formatBankAccount(row.account) }}
              </div>

              <div>
                <VLink
                  icon-postfix
                  @click="showRequisites($event, row)"
                >
                  Реквизиты
                  <template #icon>
                    <VIcon material="expand_more"/>
                  </template>
                </VLink>
              </div>
            </template>
            <template v-else>
              --
            </template>
          </template>
        </VDataGridColumnV2>

        <VDataGridColumnV2
          id="name"
          width="200px"
          label="Банк"
        >
          <template #default="{ row }">
            <template v-if="row.bank">
              <div :title="row.bank.name">
                {{ row.bank.name }}
              </div>
              <VBadges>
                <VBadge v-if="row.isMain">Основной</VBadge>
              </VBadges>
            </template>
            <template v-else>
              --
            </template>
          </template>
        </VDataGridColumnV2>

        <VDataGridColumnV2
          id="currency"
          label="Валюта"
        >
          <template #default="{ row }">
            <template v-if="row.currencyCode">
              {{ $core.currencies.currencies.get(row.currencyCode).description }}
            </template>
            <template v-else>
              --
            </template>
          </template>
        </VDataGridColumnV2>
      </VDataGridV2>
    </div>

    <VBankAccountPopup
      :visible="bankAccountPopupVisible"
      :target="bankAccountPopup.target"
      :bank-account="bankAccountPopup.entity"
      :partner="localPartner"
      @close="hideRequisites"
    />
  </section>
</template>

<script lang="ts">

import Partner             from "@/domains/Partners/Partner";
import BankAccount         from "@/domains/BankAccounts/BankAccount";
import BankAccountsService from "@/domains/BankAccounts/BankAccountsService";

import BankAccountPopup from '@/domains/BankAccounts/components/BankAccountPopup/BankAccountPopup.vue';

export default defineComponent({
  name: 'BankAccountsSection',

  components: {
    VBankAccountPopup: BankAccountPopup,
  },

  props: {
    localPartner: {
      type:    Partner,
      default: null,
    },
  },

  emits: [
    'bank-accounts:updated',
  ],

  setup() {
    return {
      BankAccountsService,
    };
  },

  data() {
    return {
      bankAccountPopup: {
        entity: null as BankAccount | null,
        target: null as HTMLElement | null,
      },
    };
  },

  computed: {
    /**
     * Компании партнёра, у которых есть р/с. Грид группирует счета по компаниям
     * (group-children-property="bankAccounts"). Данные берутся прямо из партнёра —
     * partners.get отдаёт companies[] с заполненными bankAccounts[]. Отдельной
     * загрузки нет: при обновлении партнёра (renewPartner в карточке) localPartner
     * переприсваивается, и грид перерисуется реактивно через :data.
     */
    companiesWithAccounts() {
      return this.localPartner.companies.filter(company => company.bankAccounts?.length > 0);
    },

    /** временная мера, чтобы не плодить ошибки */

    /** null — добавлять можно: у партнёра есть юр. лицо РФ или Узбекистана */
    createBankAccountDisabledReason(): string | null {
      const hasAllowedCompany = this.localPartner.companies.some(company => {
        const countryCode = company.country?.code ?? null;

        return countryCode === 'RU' || countryCode === 'UZ';
      });

      if (hasAllowedCompany === true) {
        return null;
      }
      else {
        return 'В разработке. Добавление р/с на данный момент доступно для юр. лиц РФ и Узбекистана';
      }
    },

    bankAccountPopupVisible() {
      return Boolean(this.bankAccountPopup.entity);
    },
  },

  methods: {
    async createBankAccount(): Promise<void> {
      if (await BankAccountsService.showBankAccountCard(null, this.localPartner) === true) {
        this.$emit('bank-accounts:updated');
      }
    },

    /** Грид с :data сам не обновляется — по результату пункта меню перечитываем партнёра */
    onContextMenuItemDone(item: unknown, row: BankAccount, result: boolean | void): void {
      if (result === true) {
        this.$emit('bank-accounts:updated');
      }
    },

    showRequisites(event: MouseEvent, bankAccount: BankAccount) {
      this.bankAccountPopup.entity = bankAccount;
      this.bankAccountPopup.target = event.target as HTMLElement;
    },

    hideRequisites() {
      this.bankAccountPopup.entity = null;
      this.bankAccountPopup.target = null;
    },
  },
});
</script>
